import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { ChevronDown, Download, FileText, Mail, MessageCircle } from "lucide-react";
import { API } from "../lib/apiClient";
import { waTarget } from "../lib/wa";
import { companyLogoSources, useCompanyLogos } from "../lib/companies";

export const faviconFor = (company, logos) => companyLogoSources(company, logos).find((u) => u.includes("google.com/s2")) || "";

export function QuotePdfMenu({ qt, onSent }) {
  const logos = useCompanyLogos();
  return (
    <PdfShareMenu base={`${API}/quotes/${qt.id}/branded-pdf`} payload={() => ({ companyLogo: faviconFor(qt.company, logos), origin: window.location.origin,
      companyLogos: Object.fromEntries((qt.offers || []).map((o) => [o.company, faviconFor(o.company, logos)])) })}
      customerName={qt.customerName} phone={qt.customerPhone} email={qt.customerEmail} testId={qt.id}
      waText={(link, data) => (data?.choicePath
        ? `${qt.customerName ? `Merhaba ${qt.customerName},` : "Merhaba,"} ${qt.branch ? `${qt.branch} ` : ""}için ${qt.offers.length} şirketten aldığım teklifleri inceleyip size uygun olanı seçebilirsiniz: ${window.location.origin}${data.choicePath} (bağlantı 7 gün geçerlidir)`
        : `${qt.customerName ? `Merhaba ${qt.customerName},` : "Merhaba,"} ${qt.branch ? `${qt.branch} ` : ""}teklifinizi PDF olarak iletiyorum: ${link} (bağlantı 7 gün geçerlidir)`)}
      mailNote={qt.pdfs?.length ? " (şirket PDF'i de ekte)" : ""} onSent={() => onSent?.(qt)} />
  );
}

export function PdfShareMenu({ base, payload, phone, email: defaultEmail, testId, waText, mailNote = "", onSent, label = "Teklif PDF", primary = false }) {
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState("");
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(null); };
    const hide = () => setOpen(null);
    document.addEventListener("mousedown", close);
    window.addEventListener("scroll", hide, true);
    window.addEventListener("resize", hide);
    return () => { document.removeEventListener("mousedown", close); window.removeEventListener("scroll", hide, true); window.removeEventListener("resize", hide); };
  }, [open]);
  const body = (extra = {}) => ({ ...payload(), ...extra });
  const run = async (key, fn) => {
    setBusy(key); setOpen(null);
    try { await fn(); } catch (e) { window.alert(e?.response?.data?.detail || "İşlem başarısız"); } finally { setBusy(""); }
  };
  const download = () => run("pdf", async () => {
    const { data, headers } = await axios.post(base, body(), { responseType: "blob" });
    const m = /filename\*=UTF-8''([^;]+)/.exec(headers["content-disposition"] || "");
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url; a.download = m ? decodeURIComponent(m[1]) : "teklif.pdf";
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  });
  const whatsapp = () => {
    const w = window.open("", "_blank");
    run("wa", async () => {
      try {
        const { data } = await axios.post(`${base}/share`, body());
        const url = `https://wa.me/${waTarget(phone)}?text=${encodeURIComponent(waText(`${process.env.REACT_APP_BACKEND_URL}${data.path}`, data))}`;
        if (w) w.location.href = url; else window.location.href = url;
        onSent?.(data);
      } catch (e) { w?.close(); throw e; }
    });
  };
  const email = () => {
    const to = window.prompt("PDF hangi adrese gönderilsin?", defaultEmail || "");
    if (!to) return;
    run("mail", async () => {
      const { data } = await axios.post(`${base}/email`, body({ to: to.trim() }));
      onSent?.(data);
      window.alert(`PDF ${to.trim()} adresine gönderildi${mailNote}`);
    });
  };
  return (
    <div className="qpdf" ref={ref}>
      <button type="button" className={primary ? "primary-button small" : "outline-button small"} onClick={(e) => {
        if (open) { setOpen(null); return; }
        const r = e.currentTarget.getBoundingClientRect();
        const below = window.innerHeight - r.bottom > 150;
        setOpen({ right: Math.max(8, window.innerWidth - r.right), ...(below ? { top: r.bottom + 4 } : { bottom: window.innerHeight - r.top + 4 }) });
      }} disabled={!!busy} data-testid={`quote-branded-pdf-${testId}`}>
        <FileText size={13} /> {busy ? "Hazırlanıyor…" : label} <ChevronDown size={12} />
      </button>
      {open && (
        <div className="qpdf-menu" style={open} data-testid={`quote-branded-pdf-menu-${testId}`}>
          <button type="button" onClick={download} data-testid={`quote-pdf-download-${testId}`}><Download size={14} /> PDF indir</button>
          <button type="button" onClick={whatsapp} data-testid={`quote-pdf-whatsapp-${testId}`}><MessageCircle size={14} /> WhatsApp ile gönder</button>
          <button type="button" onClick={email} data-testid={`quote-pdf-email-${testId}`}><Mail size={14} /> E-posta ile gönder</button>
        </div>
      )}
    </div>
  );
}
